import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import Modal from 'components/atoms/Modal'
import SearchableSelect from 'components/SearchableSelect'
import { SkeletonLine } from 'components/Skeleton'
import organisationService from 'services/organisationService'
import { hideEmail } from 'utils/user'
import { ACCENT, BORDER, INK, MUTED } from 'features/actions/workspace/ui'

const emailDomain = email => (email || '').trim().toLowerCase().split('@')[1] || ''

const emailMatchesDomains = (email, domains) => {
  const domain = emailDomain(email)
  return !!domain && domains.includes(domain)
}

const AddSuperAdminModal = ({ open, onClose, domains = [], onAdded }) => {
  const [mode, setMode] = useState('colleague')
  const [colleagues, setColleagues] = useState([])
  const [loadingColleagues, setLoadingColleagues] = useState(false)
  const [userId, setUserId] = useState(null)
  const [email, setEmail] = useState('')
  const [emailTouched, setEmailTouched] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open) return
    setMode('colleague')
    setUserId(null)
    setEmail('')
    setEmailTouched(false)
    setError('')
    setLoadingColleagues(true)
    organisationService.colleagues('')
      .then(res => setColleagues((res?.data?.data || []).filter(c => !c.isSuperAdmin)))
      .catch(() => setColleagues([]))
      .finally(() => setLoadingColleagues(false))
  }, [open])

  const emailInvalid = emailTouched && email.trim() && !emailMatchesDomains(email, domains)
  const canSubmit = mode === 'colleague' ? !!userId : (!!email.trim() && emailMatchesDomains(email, domains))

  const submit = async () => {
    if (mode === 'email' && !emailMatchesDomains(email, domains)) {
      setEmailTouched(true)
      return
    }
    setError('')
    setSubmitting(true)
    try {
      const chosen = mode === 'colleague' ? colleagues.find(c => c.id === userId) : null
      const body = mode === 'colleague' ? { userId } : { email: email.trim() }
      const res = await organisationService.addSuperAdmin(body)
      onAdded?.(res?.data?.status, chosen ? chosen.name : email.trim())
    } catch (err) {
      setError(err?.response?.data?.errors?.email?.[0] || err?.response?.data?.message || 'We could not add this Super Admin. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const colleagueOptions = colleagues.map(c => ({
    value: c.id,
    label: c.name,
    sublabel: `${hideEmail(c.email || null)}${c.isAdmin ? ' · Admin' : ''}`,
  }))

  return (
    <Modal
      open={open}
      onClose={onClose}
      title='Add a Super Admin'
      withCancel
      cancelButtonText='Cancel'
      onCancel={onClose}
      withSubmit
      submitButtonText='Add Super Admin'
      disabled={submitting || !canSubmit}
      onSubmit={submit}
    >
      <Body>
        <Choices role='group' aria-label='Who to add'>
          <ChoiceButton type='button' aria-pressed={mode === 'colleague'} $on={mode === 'colleague'} onClick={() => setMode('colleague')}>
            A colleague on Building Passport
          </ChoiceButton>
          <ChoiceButton type='button' aria-pressed={mode === 'email'} $on={mode === 'email'} onClick={() => setMode('email')}>
            Someone without an account yet
          </ChoiceButton>
        </Choices>

        {mode === 'colleague' ? (
          loadingColleagues ? (
            <SkeletonLine $h={40} />
          ) : colleagues.length === 0 ? (
            <Empty>Nobody in your organisation can be added right now. Everyone eligible is already a Super Admin.</Empty>
          ) : (
            <Field>
              <Label htmlFor='colleague-picker'>Choose a colleague</Label>
              <SearchableSelect
                id='colleague-picker'
                ariaLabel='Choose a colleague'
                placeholder='Search by name or email'
                value={userId}
                options={colleagueOptions}
                onChange={setUserId}
              />
            </Field>
          )
        ) : (
          <Field>
            <Label htmlFor='super-admin-email'>Email address</Label>
            <Input
              id='super-admin-email'
              type='email'
              value={email}
              onChange={e => setEmail(e.target.value)}
              onBlur={() => setEmailTouched(true)}
              placeholder='name@yourcompany.com'
            />
            {emailInvalid && (
              <HelpError>Use an email address on one of your organisation&apos;s domains: {domains.join(', ')}</HelpError>
            )}
          </Field>
        )}

        {error && <HelpError>{error}</HelpError>}
      </Body>
    </Modal>
  )
}

export default AddSuperAdminModal

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const Choices = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const ChoiceButton = styled.button`
  flex: 1 1 200px;
  min-height: 44px;
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid ${({ $on }) => ($on ? ACCENT : BORDER)};
  background: ${({ $on }) => ($on ? '#f0fbfc' : '#fff')};
  color: ${INK};
  font: inherit;
  font-size: 15px;
  font-weight: ${({ $on }) => ($on ? 700 : 600)};
  cursor: pointer;
  text-align: left;
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const Label = styled.label`
  font-size: 15px;
  font-weight: 600;
  color: ${INK};
`

const Input = styled.input`
  min-height: 44px;
  padding: 0 14px;
  border: 1px solid ${BORDER};
  border-radius: 8px;
  font-size: 15px;
  font-family: inherit;

  &:focus { outline: 2px solid ${ACCENT}; outline-offset: 1px; }
`

const HelpError = styled.p`
  margin: 0;
  font-size: 14px;
  color: #b00020;
`

const Empty = styled.p`
  margin: 0;
  font-size: 15px;
  color: ${MUTED};
`
